import { useEffect } from "react";

/**
 * @typedef {Object} IntersectionObserverProps
 * @property {IntersectionObserverCallback} onIntersect - Callback function to be called when the target element intersects the viewport.
 * @property {IntersectionObserverInit} [options] - Options to customize the behavior of the IntersectionObserver.
 */

/**
 * Custom React hook to observe when a DOM element enters the viewport.
 * @param {{ current: HTMLElement | null }} ref - Ref of the element to observe.
 * @param {IntersectionObserverProps} props - Props for customizing IntersectionObserver behavior.
 */
export default function useIntersectionObserver(ref, { onIntersect, options }) {
  useEffect(() => {
    const current = ref.current;
    if (!current) {
      return;
    }

    const observer = new IntersectionObserver(onIntersect, options);

    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [ref, onIntersect, options]);
}
